import { createContext, useCallback, useContext } from 'react';

import {
  atualizarConfiguracao,
  atualizarPerfil,
  type Configuracao,
  type ConfiguracaoInput,
  type Participante,
} from '@/api/secretario';

/**
 * O advogado cujo perfil está sendo editado no Secretário do escritório
 * (docs/PLANO_SECRETARIO_ESCRITORIO.md §4.2–4.4). Os cartões de áreas, honorários e
 * modelos são os mesmos do Secretário pessoal: dentro deste contexto, eles mostram
 * as áreas DELE e gravam no perfil DELE — fora, na configuração, como sempre.
 */
export interface AdvogadoEmEdicao {
  id: string;
  nome: string;
  /** Sem honorários próprios: valem os do escritório (§4.4). */
  herdaHonorarios?: boolean;
  /** Sem modelos próprios: valem os do escritório. */
  herdaModelos?: boolean;
  /** Os modelos que são DELE — a herança é campo a campo (``modelo_efetivo``). */
  contratoProprio?: boolean;
  procuracaoPropria?: boolean;
  /** O escritório tem os seus? Sem eles, "usar os do escritório" seria ficar sem nada. */
  escritorioTemHonorarios?: boolean;
  escritorioTemModelos?: boolean;
}

const Contexto = createContext<AdvogadoEmEdicao | null>(null);

export const AdvogadoEmEdicaoProvider = Contexto.Provider;

export function useAdvogadoEmEdicao(): AdvogadoEmEdicao | null {
  return useContext(Contexto);
}

export function nomeDoParticipante(p: Participante): string {
  return p.nome_tratamento || p.nome;
}

/**
 * A configuração vista por um advogado: as áreas dele e, no lugar dos padrões da
 * configuração, o que VALE para ele — os do perfil, ou os do escritório quando ele
 * não tem os próprios (a mesma ordem do backend, ``honorarios_efetivos``). É o que os
 * cartões de sempre leem; o resto (canal, base, prontidão geral) segue igual.
 */
export function projetarParaAdvogado(config: Configuracao, advogadoId: string): Configuracao {
  const perfil = config.participantes?.find((p) => p.usuario_id === advogadoId);
  const areas = config.areas.filter((a) => (a.advogado_responsavel_id ?? config.usuario_id) === advogadoId);
  const ids = new Set(areas.map((a) => a.id));
  return {
    ...config,
    areas,
    honorarios: perfil?.honorarios ?? config.honorarios ?? null,
    template_contrato_id: perfil?.template_contrato_id ?? config.template_contrato_id ?? null,
    template_procuracao_id: perfil?.template_procuracao_id ?? config.template_procuracao_id ?? null,
    advogado_endereco: perfil?.advogado_endereco ?? config.advogado_endereco ?? null,
    advogado_tratamento: perfil?.tratamento ?? null,
    advogado_nome_tratamento: perfil?.nome_tratamento ?? null,
    prontidao: { ...config.prontidao, por_area: config.prontidao.por_area.filter((a) => ids.has(a.area_id)) },
  };
}

type Padroes = Pick<ConfiguracaoInput, 'honorarios' | 'template_contrato_id' | 'template_procuracao_id'>;

/** Grava honorários e modelos padrão: no perfil do advogado em edição, ou na configuração. */
export function useGravarPadroes(): (payload: Padroes) => Promise<Configuracao> {
  const advogado = useAdvogadoEmEdicao();
  return useCallback(
    (payload: Padroes) => (advogado ? atualizarPerfil(advogado.id, payload) : atualizarConfiguracao(payload)),
    [advogado],
  );
}
